"use client";

import Link from "next/link";
import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Button from "../../Common/Buttons/Buttons";
import { useUserSigninMutation, useGoogleSigninMutation } from "@/Redux/apis/auth";
import { useRouter, useSearchParams } from "next/navigation";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useRepsSigninMutation } from "@/Redux/apis/reps";
import Cookies from "js-cookie";
import { userData } from "@/Redux/apis/userData";
import { toast } from "react-toastify";
import { GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import Loader from "../../Loader/Loader";
import { getLoginErrorMessage } from "@/utils/loginRequest";
import { repPostLoginDestination } from "@/services/repInvitations";
import { savePendingSignIn } from "@/utils/twoFactorPending";
const Login = () => {
  const [activeTab, setActiveTab] = useState(1);
  const params = useSearchParams();
  const loginButtonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState('100%');
  const handleTabClick = tabNumber => {
    setActiveTab(tabNumber);
  };
  useEffect(() => {
    let paramsType = params.get("usertype");
    if (paramsType) {
      if (paramsType == "providers") {
        handleTabClick(1);
      } else if (paramsType == "reps") {
        handleTabClick(2);
      } else {
        handleTabClick(1);
      }
    }
  }, [params]);
  const [error, setError] = useState("");
  const {
    register,
    handleSubmit,
    setValue,
    formState: {
      errors
    }
  } = useForm();
  const dispatch = useDispatch();
  const router = useRouter();
  const [userSiginApi, response] = useUserSigninMutation();
  const [googleSigninApi, googleResponse] = useGoogleSigninMutation();
  const [signin, setSignin] = useState({
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigatingTo, setNavigatingTo] = useState("");

  // Two-factor accounts: the password step returns a code-pending pass, not an
  // access token. Keep the pass for this tab, clear any earlier session, and
  // send the user to the code screen — nothing counts as logged in until the
  // code is entered.
  const startTwoFactor = (signedIn, account, path) => {
    Cookies.remove("token");
    Cookies.remove("isReps");
    // Going Back from the code screen shows this page again while the user is
    // still logged out — don't leave the password sitting in the form.
    setSignin(current => ({
      ...current,
      password: ""
    }));
    setRepsSignin(current_0 => ({
      ...current_0,
      password_cl: ""
    }));
    setValue("password", "");
    setValue("password_cl", "");
    savePendingSignIn({
      pendingToken: signedIn.pendingToken,
      email: signedIn.email,
      account,
      userInvite: params.get("userInvite")
    });
    setIsNavigating(true);
    setNavigatingTo(path);
    router.push(path);
    toast.success("Verification OTP sent successfully");
  };
  const onChangeHandler = e => {
    const {
      name,
      value
    } = e.target;
    setValue(name, e.target.value);
    setSignin({
      ...signin,
      [name]: value
    });
  };
  const onSubmit = async e_0 => {
    setError("");
    setLoading(true);
    const payload = {
      email: signin?.email,
      password: signin?.password
    };
    await userSiginApi(payload);
  };
  useEffect(() => {
    const handleResponse = async () => {
      try {
        if (response?.isLoading) {
          setLoading(true);
        } else if (!response?.isLoading && response?.isSuccess) {
          if (response?.data?.user?.twoFactorPending) {
            startTwoFactor(response.data.user, "user", "/provider/two-factor-verification");
            setLoading(false);
            return;
          }
          if (!response?.data?.user?.token) throw new Error("Missing login token");
          const data = {
            token: response?.data?.user?.token
          };
          await dispatch(userData(data)).unwrap(); // Wait for userData to be updated

          Cookies.set("token", response?.data?.user?.token);
          Cookies.set("isReps", response?.data?.user?.reps);
          if (response?.data?.user?.twoFactorStatus === true) {
            setIsNavigating(true);
            setNavigatingTo("/provider/two-factor-verification");
            router.push("/provider/two-factor-verification");
            toast.success("Verification OTP sent successfully");
          } else {
            setIsNavigating(true);
            const destination = params.get("userInvite") ? `/accept-invite?token=${encodeURIComponent(params.get("userInvite"))}` : response?.data?.user?.userInvitationId ? "/onboarding" : "/provider/dashboard";
            setNavigatingTo(destination);
            window.location.href = destination;
            toast.success("Welcome to dashboard");
          }
          setLoading(false);
        } else if (response?.isError) {
          setError(getLoginErrorMessage(response.error));
          setLoading(false);
        }
      } catch (error_0) {
        setError(getLoginErrorMessage(error_0));
        setIsNavigating(false);
      } finally {
        if (response?.isSuccess || response?.isError) setLoading(false);
      }
    };
    handleResponse();
  }, [response, dispatch, router]);

  // Handle navigation with loading state
  const handleNavigation = href => {
    if (href === router.pathname) return;
    setIsNavigating(true);
    setNavigatingTo(href);
    router.push(href);
  };
  const [repsSiginApi, repsResponse] = useRepsSigninMutation();
  const [repsSignin, setRepsSignin] = useState({
    email_cl: "",
    password_cl: ""
  });
  const repsOnChangeHandler = e_1 => {
    const {
      name: name_0,
      value: value_0
    } = e_1.target;
    setValue(name_0, e_1.target.value);
    setRepsSignin({
      ...repsSignin,
      [name_0]: value_0
    });
  };
  const submitRepsSignin = async e_2 => {
    setError("");
    setLoading(true);
    const payload_0 = {
      email: repsSignin?.email_cl,
      password: repsSignin?.password_cl,
      ...(params.get("repInvite") ? {
        invitationToken: params.get("repInvite")
      } : {})
    };
    await repsSiginApi(payload_0);
  };
  useEffect(() => {
    const handleRepsResponse = async () => {
      try {
        if (repsResponse?.isLoading) {
          setLoading(true);
        } else if (!repsResponse?.isLoading && repsResponse?.isSuccess) {
          if (repsResponse?.data?.user?.twoFactorPending) {
            startTwoFactor(repsResponse.data.user, "rep", "/reps/two-factor-verification");
            setLoading(false);
            return;
          }
          if (!repsResponse?.data?.user?.token) throw new Error("Missing login token");
          const data_0 = {
            token: repsResponse?.data?.user?.token
          };
          await dispatch(userData(data_0)).unwrap(); // Wait for userData to be updated

          Cookies.set("token", repsResponse?.data?.user?.token);
          Cookies.set("isReps", repsResponse?.data?.user?.reps);
          if (repsResponse?.data?.user?.twoFactorStatus === true) {
            setIsNavigating(true);
            setNavigatingTo("/reps/two-factor-verification");
            router.push("/reps/two-factor-verification");
            toast.success("Verification OTP sent successfully");
          } else {
            setIsNavigating(true);
            const destination_0 = params.get("userInvite") ? `/accept-invite?token=${encodeURIComponent(params.get("userInvite"))}` : await repPostLoginDestination();
            setNavigatingTo(destination_0);
            window.location.href = destination_0;
            toast.success("Welcome to the dashboard");
          }
          setLoading(false);
        } else if (repsResponse?.isError) {
          setError(getLoginErrorMessage(repsResponse.error));
          setLoading(false);
        }
      } catch (error_1) {
        setError(getLoginErrorMessage(error_1));
        setIsNavigating(false);
      } finally {
        if (repsResponse?.isSuccess || repsResponse?.isError) setLoading(false);
      }
    };
    handleRepsResponse();
  }, [repsResponse, dispatch, router]);

  // Handle Google authentication response
  useEffect(() => {
    const handleGoogleResponse = async () => {
      try {
        if (googleResponse?.isLoading) {
          setLoading(true);
        } else if (!googleResponse?.isLoading && googleResponse?.isSuccess) {
          if (googleResponse?.data?.user?.twoFactorPending) {
            const reps = googleResponse.data.user.reps === true;
            startTwoFactor(googleResponse.data.user, reps ? "rep" : "user", reps ? "/reps/two-factor-verification" : "/provider/two-factor-verification");
            setLoading(false);
            return;
          }
          if (!googleResponse?.data?.user?.token) throw new Error("Missing login token");
          const data_1 = {
            token: googleResponse?.data?.user?.token
          };
          await dispatch(userData(data_1)).unwrap();
          Cookies.set("token", googleResponse?.data?.user?.token);
          Cookies.set("isReps", googleResponse?.data?.user?.reps);
          if (googleResponse?.data?.user?.twoFactorStatus === true) {
            const redirectPath = activeTab === 1 ? "/provider/two-factor-verification" : "/reps/two-factor-verification";
            setIsNavigating(true);
            setNavigatingTo(redirectPath);
            router.push(redirectPath);
            toast.success("Verification OTP sent successfully");
          } else {
            const dashboardPath = params.get("userInvite") ? `/accept-invite?token=${encodeURIComponent(params.get("userInvite"))}` : activeTab === 1 ? googleResponse?.data?.user?.userInvitationId ? "/onboarding" : "/provider/dashboard" : await repPostLoginDestination();
            setIsNavigating(true);
            setNavigatingTo(dashboardPath);
            window.location.href = dashboardPath;
            toast.success("Welcome to dashboard");
          }
          setLoading(false);
        } else if (googleResponse?.isError) {
          setError(getLoginErrorMessage(googleResponse.error));
          setLoading(false);
        }
      } catch (error_2) {
        setError(getLoginErrorMessage(error_2));
        setIsNavigating(false);
      } finally {
        if (googleResponse?.isSuccess || googleResponse?.isError) setLoading(false);
      }
    };
    handleGoogleResponse();
  }, [googleResponse, dispatch, router, activeTab]);

  // Calculate button width dynamically
  useEffect(() => {
    const calculateButtonWidth = () => {
      if (loginButtonRef.current) {
        const width = loginButtonRef.current.offsetWidth;
        setButtonWidth(`${width}px`);
      }
    };
    calculateButtonWidth();
    window.addEventListener('resize', calculateButtonWidth);
    return () => {
      window.removeEventListener('resize', calculateButtonWidth);
    };
  }, [activeTab]);

  // Override Google button inline styles
  useEffect(() => {
    const overrideGoogleButtonStyles = () => {
      // Target Google button elements
      const googleButtons = document.querySelectorAll('.nsm7Bb-HzV7m-LgbsSe');
      googleButtons.forEach(button => {
        if (button.style.maxWidth || button.style.minWidth || button.style.width) {
          // Override inline styles
          button.style.setProperty('width', '100%', 'important');
          button.style.setProperty('max-width', '600px', 'important');
          button.style.setProperty('min-width', '100%', 'important');
        }
      });
    };

    // Run immediately
    overrideGoogleButtonStyles();

    // Set up a MutationObserver to watch for Google button changes
    const observer = new MutationObserver(() => {
      overrideGoogleButtonStyles();
    });

    // Observe the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Also run on a slight delay to catch late-loading elements
    const timeoutId = setTimeout(overrideGoogleButtonStyles, 1000);
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [buttonWidth]);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const togglePasswordVisibility = () => {
    setPasswordVisible(!isPasswordVisible);
  };

  // Google One Tap Login Handler
  const handleGoogleSuccess = async credentialResponse => {
    try {
      setError("");
      const decoded = jwtDecode(credentialResponse.credential);
      const googleUserData = {
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        googleId: decoded.sub
      };

      // Create payload for your backend API
      const payload_1 = {
        email: googleUserData.email,
        name: googleUserData.name,
        googleId: googleUserData.googleId,
        picture: googleUserData.picture,
        credential: credentialResponse.credential,
        ...(activeTab === 2 && params.get('repInvite') ? {
          invitationToken: params.get('repInvite')
        } : {}),
        loginType: 'google',
        userType: activeTab === 1 ? 'provider' : 'reps'
      };
      // Call the Google signin API
      await googleSigninApi(payload_1);
    } catch (error_3) {
      setLoading(false);
      setError(getLoginErrorMessage(error_3));
      toast.error('Google login failed. Please try again.');
    }
  };
  const handleGoogleError = () => {
    console.error('Google login failed');
    toast.error('Google login failed. Please try again.');
  };

  // Google One Tap Login Hook - Only enable if client ID exists
  // useGoogleOneTapLogin({
  //   onSuccess: handleGoogleSuccess,
  //   onError: (error) => {
  //     console.log('Google One Tap Error:', error);
  //     // Don't show error toast for One Tap failures as they're often expected
  //   },
  //   disabled: !process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
  //   auto_select: false, // Disable auto-select to avoid issues
  //   cancel_on_tap_outside: true,
  //   prompt_parent_id: 'google-one-tap-container', // Optional: specify container
  // });

  return <>
      {isNavigating && <Loader />}
      <div className="flex flex-row h-screen ">
        <div className="w-[50%] hidden lg:block sign-up-bg overflow-hidden">
          <div className="h-screen px-8 xl:px-20 py-10 flex items-center justify-between flex-col">
            <div className="flex-1 flex items-center">
              <div className="self-stretch flex-col justify-center mx-auto items-start gap-6 inline-flex">
                <div className="self-stretch flex-col flex justify-center text-neutral-50 text-5xl font-semibold font-poppins leading-[58px]">
                  <span>Empowering</span>
                  <span>Physicians, </span>
                  <span>Advancing Healthcare</span>
                </div>
                <div className="self-stretch max-w-[450px] p-0.5 opacity-95 justify-start items-start gap-2.5 inline-flex">
                  <div className="grow shrink basis-0 text-zinc-200 text-lg font-light font-poppins leading-[27px]">
                    Harnessing Cutting-Edge Technology for Better Patient Outcomes
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch flex-col justify-start items-center gap-5 inline-flex pb-6">
              <div className="pl-[25px] pr-[26px] pt-[26px] pb-[42px] bg-white bg-opacity-10 rounded-2xl border border-white border-opacity-50 backdrop-blur-[12px] justify-center items-center inline-flex">
                <div className="self-stretch flex-col justify-start items-start gap-[22px] inline-flex">
                  <div className="max-w-[508px] p-0.5 justify-start items-start gap-2.5 inline-flex">
                    <div className="grow shrink basis-0 text-gray-100 text-base font-light font-poppins leading-normal">
                      Alpha.MD has saved me a tremendous amount of time.
                      Instead of spending hours researching medications on my
                      own, I can quickly see how my peers are actually using
                      and experiencing different treatments through their
                      reviews. These real-world insights from other physicians
                      have been incredibly valuable. I highly recommend it.
                    </div>
                  </div>
                  <div className="justify-start items-center gap-2.5 inline-flex">
                    <Image src={"/Images/doctor.png"} alt="doc" width={50} height={50} />
                    <div className="w-[108px] flex-col justify-start items-start gap-0.5 inline-flex">
                      <div className="self-stretch text-neutral-100 text-base font-semibold font-poppins">
                        Daphne Park
                      </div>
                      <div className="self-stretch text-white text-opacity-80 text-sm font-light font-poppins">
                        Physician
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-[50%] lg:h-screen flex justify-center items-start pt-4 sm:pt-10 overflow-y-auto">
          <div className="bg-white w-full px-4 sm:px-10 pb-10 flex-col justify-start items-start inline-flex">
            <div>
              <Image src={"/Images/AlphaMdLogo.png"} alt="logo" className="h-6 sm:h-8 w-auto" height={30} width={154} />
              <p className="text-[#7C7C8D] text-[8px] font-semibold tracking-[0.15em] mt-1.5">EVIDENCE &bull; EXPERIENCE &bull; OUTCOMES</p>
              <div className="flex items-center gap-2">
                <span className="text-[#7C7C8D] text-[8px] font-semibold tracking-[0.15em] relative -top-[2px]">INTELLIGENCE BY</span>
                <Image src="/Images/website/mdReviewer.svg" width={80} height={16} alt="MDReviewer" className="h-[44px] w-auto object-contain" />
              </div>
            </div>
            <div className="flex-col w-full justify-start pt-4 sm:pt-6 items-start gap-4 sm:gap-8 inline-flex">
              <div className="flex-col justify-center items-start gap-2 flex">
                <div className="text-stone-900 text-2xl sm:text-[32px] font-semibold font-poppins">
                  Log In
                </div>
                <div className="mt-1 sm:mt-2 w-full text-neutral-400 text-xs sm:text-sm font-medium font-poppins">
                  Log into your account with your credentials
                </div>
              </div>
            </div>
            <div className="flex-col justify-start items-start gap-4 sm:gap-8 pt-4 sm:pt-6 inline-flex w-full">
              <div className="w-full flex-col justify-start items-start gap-x-4 flex">
                <div className="w-full flex-col justify-center items-start gap-2 flex">
                  <div className="w-full self-stretch text-neutral-400 text-xs sm:text-sm font-medium font-poppins">
                    Choose your account type
                  </div>
                  <div className="self-stretch w-full justify-start flex-col lg:flex-row items-start gap-3 sm:gap-4 flex">
                    <div className={`flex items-center w-full lg:w-[50%] cursor-pointer py-3 px-4 border flex-1 rounded-[10px] ${activeTab === 1 ? "bg-blue-600 bg-opacity-10 border-blue-600 text-blue-600" : "border-stone-300 text-neutral-600"}`} onClick={() => handleTabClick(1)}>
                      <div className="self-stretch justify-start items-center gap-4 inline-flex">
                        <div className="w-6 h-6 relative">
                          <div className="w-[19.40px] h-[21.60px] ">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M18.1216 13.7103C18.327 14.1845 18.4466 14.7648 18.473 15.4359C19.1663 15.5947 19.6852 16.2149 19.6852 16.9555C19.6852 17.8157 18.9854 18.5155 18.1252 18.5155C17.265 18.5155 16.5652 17.8157 16.5652 16.9555C16.5652 16.2233 17.0728 15.6096 17.7539 15.4419C17.7239 14.7754 17.5646 13.7614 16.9122 13.2607C16.5794 13.1746 16.2371 13.1122 15.886 13.08C15.8601 14.2476 12.0009 17.1291 12.0009 17.1291C12.0009 17.1291 8.14049 14.2481 8.11433 13.0805C7.72433 13.116 7.34489 13.1873 6.97841 13.29C6.50249 13.6668 6.25625 14.3688 6.24353 15.3893C6.38345 15.4697 6.49649 15.5952 6.56585 15.7471C7.07849 15.9893 7.53137 16.4986 7.87817 17.2236C7.93625 17.3455 7.94393 17.4833 7.90121 17.6091C8.10905 18.144 8.22785 18.7287 8.22785 19.2197C8.22785 19.9078 8.22785 20.5584 7.47785 20.7255C7.39673 20.7929 7.29497 20.8301 7.18817 20.8301H6.68705C6.43577 20.8301 6.23129 20.6251 6.23129 20.3746L6.23177 20.3568C6.24137 20.1144 6.44417 19.9188 6.68705 19.9188H7.18817C7.23953 19.9188 7.28969 19.9275 7.33817 19.9443C7.36841 19.9359 7.37801 19.9289 7.37825 19.9287C7.43393 19.8298 7.43393 19.4179 7.43393 19.2202C7.43393 18.8213 7.33409 18.3396 7.15913 17.892C7.06841 17.8404 6.99497 17.7624 6.94985 17.6681C6.64601 17.0319 6.23273 16.621 5.89721 16.621C5.55401 16.621 5.12009 17.0655 4.81745 17.7259C4.76801 17.8337 4.68137 17.9213 4.57481 17.9739C4.41665 18.402 4.32977 18.8429 4.32977 19.2199C4.32977 19.386 4.32977 19.8267 4.39313 19.9303C4.39385 19.9303 4.40801 19.939 4.44785 19.9488C4.49969 19.9289 4.55537 19.9186 4.61105 19.9186H5.11313C5.34713 19.9186 5.54273 20.0967 5.56625 20.3283L5.56865 20.3563C5.56865 20.6254 5.36417 20.8303 5.11313 20.8303H4.61105C4.51145 20.8303 4.41521 20.797 4.33601 20.7365C4.04921 20.6825 3.84713 20.556 3.71921 20.3501C3.56417 20.1012 3.53585 19.7743 3.53585 19.2199C3.53585 18.7337 3.64817 18.1735 3.85217 17.6386C3.82217 17.5241 3.83225 17.4046 3.88217 17.2968C4.09457 16.8322 4.36481 16.4343 4.66337 16.1465C4.82753 15.9883 5.00393 15.8611 5.18849 15.7683C5.25761 15.6065 5.37761 15.4738 5.52425 15.39C5.53193 14.7504 5.63033 14.1982 5.81225 13.7429C3.72953 14.7994 2.30225 16.9601 2.30225 19.4554C2.30225 22.644 4.63193 22.8 7.68161 22.8C8.01449 22.8 8.35529 22.7981 8.70353 22.7981H15.2956C15.6434 22.7981 15.9842 22.8 16.317 22.8C19.3672 22.8 21.6978 22.644 21.6978 19.4554C21.6976 16.9337 20.2398 14.754 18.1216 13.7103Z" fill={activeTab === 1 ? "#354EF0" : "#8E8E8E"} />
                              <path d="M12.0019 13.9181C14.9501 13.9181 17.3398 9.48505 17.3398 6.53713C17.3398 3.58969 14.9501 1.20001 12.0019 1.20001C9.05423 1.20001 6.66455 3.58969 6.66455 6.53713C6.66455 9.48505 9.05423 13.9181 12.0019 13.9181Z" fill={activeTab === 1 ? "#354EF0" : "#8E8E8E"} />
                              <path d="M18.125 17.6755C18.5227 17.6755 18.845 17.3532 18.845 16.9555C18.845 16.5579 18.5227 16.2355 18.125 16.2355C17.7274 16.2355 17.405 16.5579 17.405 16.9555C17.405 17.3532 17.7274 17.6755 18.125 17.6755Z" fill={activeTab === 1 ? "#354EF0" : "#8E8E8E"} />
                            </svg>
                          </div>
                        </div>
                        <div className={activeTab === 1 ? "text-blue-600 text-base font-medium  font-poppins" : "text-neutral-600 text-base font-medium  font-poppins"}>
                          Physician / Clinic
                        </div>
                      </div>
                    </div>
                    <div className={`flex items-center w-full lg:w-[50%] cursor-pointer py-3 px-4 flex-1 border rounded-[10px] ${activeTab === 2 ? "bg-blue-600 bg-opacity-10 border-blue-600 text-blue-600" : "border-stone-300 text-neutral-600"}`} onClick={() => handleTabClick(2)}>
                      <div className="self-stretch justify-start items-center gap-4 inline-flex">
                        <div className="w-6 h-6 relative">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M22.6571 18.459C22.6853 18.3746 22.7013 18.2842 22.7013 18.1903V8.32235C22.7013 7.85457 22.3219 7.47541 21.8541 7.47541H17.1389V17.9787C17.1389 18.1474 17.1084 18.3086 17.0546 18.459H16.5015C16.5297 18.3746 16.5457 18.2842 16.5457 18.1903V4.63747C16.5457 4.16968 16.1663 3.79053 15.6985 3.79053H8.30102C7.83324 3.79053 7.45408 4.16968 7.45408 4.63747V18.1903C7.45408 18.2842 7.47008 18.3746 7.49827 18.459H6.94541C6.89157 18.3086 6.8611 18.1474 6.8611 17.9787V7.47541H2.25893C1.79114 7.47541 1.41199 7.85457 1.41199 8.32235V18.1903C1.41199 18.2842 1.42799 18.3746 1.45592 18.459H0V20.2097H24V18.459H22.6571ZM3.95281 14.9996H2.73865V13.7857H3.95281V14.9996ZM3.95281 12.8681H2.73865V11.654H3.95281V12.8681ZM3.95281 10.807H2.73865V9.59289H3.95281V10.807ZM5.84477 14.9996H4.63061V13.7857H5.84477V14.9996ZM5.84477 12.8681H4.63061V11.654H5.84477V12.8681ZM5.84477 10.807H4.63061V9.59289H5.84477V10.807ZM13.8847 18.4023H10.32V13.9551H13.8847V18.4023ZM11.9999 10.1432C10.3509 10.1432 9.01413 8.80639 9.01413 7.15746C9.01413 5.50828 10.3512 4.17146 11.9999 4.17146C13.6491 4.17146 14.9859 5.50828 14.9859 7.15746C14.9859 8.80664 13.6491 10.1432 11.9999 10.1432ZM19.7082 14.9996H18.4943V13.7857H19.7082V14.9996ZM19.7082 12.8681H18.4943V11.654H19.7082V12.8681ZM19.7082 10.807H18.4943V9.59289H19.7082V10.807ZM21.5999 14.9996H20.386V13.7857H21.5999V14.9996ZM21.5999 12.8681H20.386V11.654H21.5999V12.8681ZM21.5999 10.807H20.386V9.59289H21.5999V10.807ZM12.7409 6.41642H13.8845V7.8985H12.7409V9.04257H11.2588V7.8985H10.1153V6.41642H11.2588V5.27286H12.7409V6.41642Z" fill={activeTab === 2 ? "#354EF0" : "#8E8E8E"} />
                          </svg>
                        </div>
                        <div className={activeTab === 2 ? "text-blue-600 text-base font-medium  font-poppins" : "text-neutral-600 text-base font-medium  font-poppins"}>
                          Reps
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {error && <div role="alert" className="text-sm mt-3 w-full bg-red-500/10 text-red-600 px-3 py-2 rounded-lg">
              {error}
            </div>}
            {activeTab === 1 && <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                <div className="flex w-full flex-col gap-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="self-stretch pt-6 flex-col justify-start items-start gap-2 flex">
                      <div className="w-full text-neutral-400 text-xs sm:text-sm font-medium font-poppins">
                        Email
                      </div>
                      <div className={`${errors.email ? "border border-[#DD4243]" : "border-blue-600"} w-full h-12 pl-3  py-3 rounded-[10px] border justify-start items-start  inline-flex`}>
                        <div className="w-6 h-6 relative flex-col justify-start items-start flex">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M21.6974 15.8335C21.6974 17.8602 20.0488 19.5062 18.0247 19.5062H5.97513C3.95102 19.5062 2.30238 17.8601 2.30238 15.8335V8.16651C2.30238 7.49011 2.48758 6.85481 2.80905 6.30956L8.68849 12.189C9.5681 13.0712 10.746 13.5573 12.0011 13.5573C13.2537 13.5573 14.4316 13.0712 15.3112 12.189L21.1907 6.30956C21.5122 6.85481 21.6973 7.49006 21.6973 8.16651V15.8335H21.6974ZM18.0246 4.49381H5.97513C5.13925 4.49381 4.36764 4.7767 3.75039 5.24737L9.68899 11.1885C10.3037 11.8006 11.1241 12.1402 12.0011 12.1402C12.8756 12.1402 13.6961 11.8006 14.3107 11.1885L20.2493 5.24737C19.6321 4.7767 18.8606 4.49381 18.0246 4.49381ZM18.0246 3.07669H5.97513C3.16914 3.07669 0.885254 5.36058 0.885254 8.16656V15.8335C0.885254 18.6421 3.16914 20.9234 5.97513 20.9234H18.0246C20.8306 20.9234 23.1145 18.6421 23.1145 15.8335V8.16651C23.1145 5.36053 20.8306 3.07669 18.0246 3.07669Z" fill="#4B4B4B" />
                          </svg>
                        </div>
                        <input {...register("email", {
                      required: "email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address"
                      }
                    })} type="email" onChange={onChangeHandler} name="email" id="email" value={signin.email} placeholder="Someone@example.com" className=" outline-none border-none px-2 w-full" />
                      </div>
                      {errors.email ? <div className="w-full text-red-500 text-xs sm:text-sm">
                          {errors.email.message}
                        </div> : ""}
                    </div>
                    <div className="self-stretch flex-col justify-start items-start gap-2 flex">
                      <div className="w-full text-neutral-400 text-xs sm:text-sm font-medium font-poppins">
                        Password
                      </div>
                      <div className={`${errors.password ? "border border-[#DD4243]" : "border-blue-600"} w-full h-12 px-3   py-3 rounded-[10px] border justify-start items-start  inline-flex`}>
                        <div className="w-6 h-6 relative flex-col justify-start items-start flex">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 8.6775H17.6025V6.3525C17.6025 4.86662 17.0122 3.44161 15.9616 2.39093C14.9109 1.34026 13.4859 0.75 12 0.75C10.5141 0.75 9.08911 1.34026 8.03843 2.39093C6.98776 3.44161 6.3975 4.86662 6.3975 6.3525V8.6775H6C5.20435 8.6775 4.44129 8.99357 3.87868 9.55618C3.31607 10.1188 3 10.8819 3 11.6775V20.25C3 21.0456 3.31607 21.8087 3.87868 22.3713C4.44129 22.9339 5.20435 23.25 6 23.25H18C18.7956 23.25 19.5587 22.9339 20.1213 22.3713C20.6839 21.8087 21 21.0456 21 20.25V11.6775C21 10.8819 20.6839 10.1188 20.1213 9.55618C19.5587 8.99357 18.7956 8.6775 18 8.6775ZM7.8975 6.3525C7.8975 5.81375 8.00361 5.28028 8.20978 4.78254C8.41595 4.2848 8.71814 3.83255 9.09909 3.45159C9.48005 3.07064 9.9323 2.76845 10.43 2.56228C10.9278 2.35611 11.4613 2.25 12 2.25C12.5387 2.25 13.0722 2.35611 13.57 2.56228C14.0677 2.76845 14.52 3.07064 14.9009 3.45159C15.2819 3.83255 15.584 4.2848 15.7902 4.78254C15.9964 5.28028 16.1025 5.81375 16.1025 6.3525V8.6775H7.8975V6.3525ZM19.5 20.25C19.5 20.6478 19.342 21.0294 19.0607 21.3107C18.7794 21.592 18.3978 21.75 18 21.75H6C5.60218 21.75 5.22064 21.592 4.93934 21.3107C4.65804 21.0294 4.5 20.6478 4.5 20.25V11.6775C4.5 11.2797 4.65804 10.8981 4.93934 10.6168C5.22064 10.3355 5.60218 10.1775 6 10.1775H18C18.3978 10.1775 18.7794 10.3355 19.0607 10.6168C19.342 10.8981 19.5 11.2797 19.5 11.6775V20.25Z" fill="#4B4B4B" />
                            <path d="M11.9999 13.4443C11.7089 13.4444 11.4246 13.5314 11.1834 13.6941C10.9421 13.8568 10.755 14.0878 10.6459 14.3576C10.5368 14.6273 10.5107 14.9235 10.571 15.2082C10.6314 15.4928 10.7753 15.753 10.9844 15.9553V17.7336C10.9844 17.9325 11.0634 18.1233 11.2041 18.2639C11.3447 18.4046 11.5355 18.4836 11.7344 18.4836H12.2654C12.4643 18.4836 12.6551 18.4046 12.7958 18.2639C12.9364 18.1233 13.0154 17.9325 13.0154 17.7336V15.9553C13.2245 15.753 13.3685 15.4928 13.4288 15.2082C13.4891 14.9235 13.4631 14.6273 13.354 14.3576C13.2449 14.0878 13.0577 13.8568 12.8165 13.6941C12.5752 13.5314 12.2909 13.4444 11.9999 13.4443Z" fill="#4B4B4B" />
                          </svg>
                        </div>
                        <input {...register("password", {
                      required: "password is required"
                    })} type={isPasswordVisible ? "text" : "password"} onChange={onChangeHandler} name="password" id="password" value={signin.password} placeholder="Enter your password" className=" outline-none border-none px-2 w-full" />
                        <div onClick={togglePasswordVisibility} className="cursor-pointer">
                          {isPasswordVisible ? <Image src={"/Images/view.svg"} height={24} width={24} alt="" /> : <Image src={"/Images/Hide.svg"} height={24} width={24} alt="" />}
                        </div>
                      </div>
                      {errors.password ? <div className="w-full text-red-500 text-xs sm:text-sm">
                          {errors.password.message}
                        </div> : ""}
                    </div>
                  </div>

                  <div className="flex w-full justify-end">
                    <a onClick={e_3 => {
                  e_3.preventDefault();
                  handleNavigation("/reset-password?role=physician");
                }} className="text-[#354EF0] underline text-end w-full cursor-pointer">
                      Forget password?
                    </a>
                  </div>
                  <button ref={loginButtonRef} className="grow w-full cursor-pointer shrink basis-0 self-stretch px-4 py-3 bg-blue-600 rounded-[10px] justify-center items-center gap-2.5 inline-flex text-white font-medium" type="submit">
                    {loading ? "Logging..." : "Log In"}
                  </button>
                  
                  {/* Divider */}
                  <div className="flex items-center my-3 sm:my-4">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-gray-500 text-xs sm:text-sm">or</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>

                  {/* Google Login Button */}
                  <div className="w-full">
                    <div className="rounded-[10px] mx-auto w-full sm:max-w-[410px] overflow-hidden border border-blue-600 hover:bg-[#f1f5ff]">
                      <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} useOneTap theme="outline" size="large" text="signin_with" shape="rectangular" width="400px" />
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-5 w-full text-center">
                    <span className="text-neutral-400 text-xs sm:text-sm font-medium text-center font-poppins">
                      Don't have an account?{" "}
                    </span>
                    <a onClick={e_4 => {
                  e_4.preventDefault();
                  handleNavigation("/sign-up");
                }}>
                      <span className="text-blue-600 text-xs sm:text-sm cursor-pointer font-medium font-poppins underline">
                        Create Account{" "}
                      </span>
                    </a>
                  </div>
                </div>
              </form>}
            {activeTab === 2 && <form className="w-full" onSubmit={handleSubmit(submitRepsSignin)}>
                <div className="flex w-full flex-col 3">
                  <div className="flex flex-col gap-4">
                    <div className="self-stretch pt-6 flex-col justify-start items-start gap-2 flex">
                      <div className="w-full text-neutral-400 text-xs sm:text-sm font-medium font-poppins">
                        Email
                      </div>
                      <div className={`${errors.email_cl ? "border border-[#DD4243]" : "border-blue-600"} w-full h-12 pl-3  py-3 rounded-[10px] border  justify-start items-start  inline-flex`}>
                        <div className="w-6 h-6 relative flex-col justify-start items-start flex">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M21.6974 15.8335C21.6974 17.8602 20.0488 19.5062 18.0247 19.5062H5.97513C3.95102 19.5062 2.30238 17.8601 2.30238 15.8335V8.16651C2.30238 7.49011 2.48758 6.85481 2.80905 6.30956L8.68849 12.189C9.5681 13.0712 10.746 13.5573 12.0011 13.5573C13.2537 13.5573 14.4316 13.0712 15.3112 12.189L21.1907 6.30956C21.5122 6.85481 21.6973 7.49006 21.6973 8.16651V15.8335H21.6974ZM18.0246 4.49381H5.97513C5.13925 4.49381 4.36764 4.7767 3.75039 5.24737L9.68899 11.1885C10.3037 11.8006 11.1241 12.1402 12.0011 12.1402C12.8756 12.1402 13.6961 11.8006 14.3107 11.1885L20.2493 5.24737C19.6321 4.7767 18.8606 4.49381 18.0246 4.49381ZM18.0246 3.07669H5.97513C3.16914 3.07669 0.885254 5.36058 0.885254 8.16656V15.8335C0.885254 18.6421 3.16914 20.9234 5.97513 20.9234H18.0246C20.8306 20.9234 23.1145 18.6421 23.1145 15.8335V8.16651C23.1145 5.36053 20.8306 3.07669 18.0246 3.07669Z" fill="#4B4B4B" />
                          </svg>
                        </div>
                        <input {...register("email_cl", {
                      required: "email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address"
                      }
                    })} type="email" onChange={repsOnChangeHandler} name="email_cl" value={repsSignin.email_cl} placeholder="Someone@example.com" className=" outline-none px-2 border-none w-full" />
                      </div>
                      {errors.email_cl ? <div className="w-full text-red-500 text-xs sm:text-sm">
                          {errors.email_cl.message}
                        </div> : ""}
                    </div>
                    <div className="self-stretch flex-col justify-start items-start gap-2 flex">
                      <div className="w-full text-neutral-400 text-xs sm:text-sm font-medium font-poppins">
                        Password
                      </div>
                      <div className={`${errors.password_cl ? "border border-[#DD4243]" : "border-blue-600"} w-full h-12 px-3 gap-2 py-3 rounded-[10px] border  justify-start items-start inline-flex`}>
                        <div className="w-6 h-6 relative flex-col justify-start items-start flex">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 8.6775H17.6025V6.3525C17.6025 4.86662 17.0122 3.44161 15.9616 2.39093C14.9109 1.34026 13.4859 0.75 12 0.75C10.5141 0.75 9.08911 1.34026 8.03843 2.39093C6.98776 3.44161 6.3975 4.86662 6.3975 6.3525V8.6775H6C5.20435 8.6775 4.44129 8.99357 3.87868 9.55618C3.31607 10.1188 3 10.8819 3 11.6775V20.25C3 21.0456 3.31607 21.8087 3.87868 22.3713C4.44129 22.9339 5.20435 23.25 6 23.25H18C18.7956 23.25 19.5587 22.9339 20.1213 22.3713C20.6839 21.8087 21 21.0456 21 20.25V11.6775C21 10.8819 20.6839 10.1188 20.1213 9.55618C19.5587 8.99357 18.7956 8.6775 18 8.6775ZM7.8975 6.3525C7.8975 5.81375 8.00361 5.28028 8.20978 4.78254C8.41595 4.2848 8.71814 3.83255 9.09909 3.45159C9.48005 3.07064 9.9323 2.76845 10.43 2.56228C10.9278 2.35611 11.4613 2.25 12 2.25C12.5387 2.25 13.0722 2.35611 13.57 2.56228C14.0677 2.76845 14.52 3.07064 14.9009 3.45159C15.2819 3.83255 15.584 4.2848 15.7902 4.78254C15.9964 5.28028 16.1025 5.81375 16.1025 6.3525V8.6775H7.8975V6.3525ZM19.5 20.25C19.5 20.6478 19.342 21.0294 19.0607 21.3107C18.7794 21.592 18.3978 21.75 18 21.75H6C5.60218 21.75 5.22064 21.592 4.93934 21.3107C4.65804 21.0294 4.5 20.6478 4.5 20.25V11.6775C4.5 11.2797 4.65804 10.8981 4.93934 10.6168C5.22064 10.3355 5.60218 10.1775 6 10.1775H18C18.3978 10.1775 18.7794 10.3355 19.0607 10.6168C19.342 10.8981 19.5 11.2797 19.5 11.6775V20.25Z" fill="#4B4B4B" />
                            <path d="M11.9999 13.4443C11.7089 13.4444 11.4246 13.5314 11.1834 13.6941C10.9421 13.8568 10.755 14.0878 10.6459 14.3576C10.5368 14.6273 10.5107 14.9235 10.571 15.2082C10.6314 15.4928 10.7753 15.753 10.9844 15.9553V17.7336C10.9844 17.9325 11.0634 18.1233 11.2041 18.2639C11.3447 18.4046 11.5355 18.4836 11.7344 18.4836H12.2654C12.4643 18.4836 12.6551 18.4046 12.7958 18.2639C12.9364 18.1233 13.0154 17.9325 13.0154 17.7336V15.9553C13.2245 15.753 13.3685 15.4928 13.4288 15.2082C13.4891 14.9235 13.4631 14.6273 13.354 14.3576C13.2449 14.0878 13.0577 13.8568 12.8165 13.6941C12.5752 13.5314 12.2909 13.4444 11.9999 13.4443Z" fill="#4B4B4B" />
                          </svg>
                        </div>
                        <input {...register("password_cl", {
                      required: "password is required"
                    })} type={isPasswordVisible ? "text" : "password"} onChange={repsOnChangeHandler} name="password_cl" value={repsSignin.password_cl} placeholder="Enter your password" className=" outline-none border-none w-full" />
                        <div onClick={togglePasswordVisibility} className="cursor-pointer">
                          {isPasswordVisible ? <Image src={"/Images/view.svg"} height={24} width={24} alt="" /> : <Image src={"/Images/Hide.svg"} width={24} height={24} alt="" />}
                        </div>
                      </div>
                      {errors.password_cl ? <div className="w-full text-red-500 text-xs sm:text-sm">
                          {errors.password_cl.message}
                        </div> : ""}
                    </div>
                  </div>
                  <div className="w-full flex items-center justify-end my-3">
                    <a onClick={e_5 => {
                  e_5.preventDefault();
                  handleNavigation("/reset-password?role=reps");
                }} className="text-[#354EF0] underline cursor-pointer">
                      Forget password?
                    </a>
                  </div>
                  <button type="submit" className="grow text-custom-16 font-medium text-white w-full cursor-pointer shrink basis-0 self-stretch px-4 py-3 bg-blue-600 rounded-[10px] justify-center items-center gap-2.5 inline-flex">
                    {loading ? "Logging..." : "Log In"}
                  </button>
                  
                  {/* Divider */}
                  <div className="flex items-center my-4 sm:my-7">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-gray-500 text-xs sm:text-sm">or</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>

                  {/* Google Login Button */}
                  <div className="w-full">
                  <div className="rounded-[10px] mx-auto w-full sm:max-w-[405px] overflow-hidden border border-blue-600">
                  <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} useOneTap theme="outline" size="large" text="signin_with" shape="rectangular" width={buttonWidth} />
                    </div>
                  </div>
                </div>
                <div className="pt-4 sm:pt-8 text-center">
                  <span className="text-neutral-400 text-xs sm:text-sm font-medium font-poppins">
                    Don't have an account?{" "}
                  </span>
                  <a onClick={e_6 => {
                e_6.preventDefault();
                handleNavigation("/reps/registration");
              }}>
                    <span className="text-blue-600 text-xs sm:text-sm cursor-pointer font-medium font-poppins underline">
                      Create Account{" "}
                    </span>
                  </a>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </>;
};
export default Login;